import { Entity, makeClone } from 'gossamer-unified';
import { Event, EventRow } from './Event';
import { BookingView, BookingViewRow } from './BookingView';
import { TicketBooking } from './TicketBooking';
import { EventProfile } from './EventProfile';

export type BookingExtendedViewRow = Omit<BookingViewRow & EventRow, 'id' | 'event_link' | 'search_vector'> & {
  event_start_at: string;
  event_end_at: string;
  permit_self_sign_esig: string;
  ticket_id: number;
};

const clone = makeClone();

export const BookingExtendedView: Entity<BookingExtendedViewRow> = {
  id: 'BookingExtendedView',
  title: 'Booking Extended',
  service: 'main',
  rowLabel: BookingView.rowLabel,
  rowLabelDynamic: BookingView.rowLabelDynamic,
  primaryKeyFields: ['ticket_booking_id'],
  source: {
    tableName: 'public.booking_extended_view',
  },
  security: BookingView.security,
  defaultSort: BookingView.defaultSort,
  defaultFilters: BookingView.defaultFilters,
  fields: {
    title: clone(Event.fields.title),
    type: clone(Event.fields.type),
    event_id: clone(BookingView.fields.event_id),
    start_at: clone(BookingView.fields.start_at),
    end_at: clone(BookingView.fields.end_at),

    // emergency backward-compatibility fixes
    event_start_at: clone(BookingView.fields.start_at),
    event_end_at: clone(BookingView.fields.end_at),

    images: clone(Event.fields.images),
    markdown_description: clone(Event.fields.markdown_description),
    plain_description: clone(Event.fields.plain_description),
    main_host_user_id: clone(Event.fields.main_host_user_id),
    event_profile_id: clone(Event.fields.event_profile_id),
    location_type: clone(Event.fields.location_type),
    location: clone(Event.fields.location),
    gps_location: clone(Event.fields.gps_location),
    livestream_session_id: clone(Event.fields.livestream_session_id),
    booking_method: clone(Event.fields.booking_method),
    booking_url: clone(Event.fields.booking_url),
    booking_payment_terms_id: clone(Event.fields.booking_payment_terms_id),
    specialty_group: clone(Event.fields.specialty_group),
    specialties: clone(Event.fields.specialties),
    employment_grade: clone(Event.fields.employment_grade),
    year_group: clone(Event.fields.year_group),
    institution_id: clone(Event.fields.institution_id),
    visibility_limit: clone(Event.fields.visibility_limit),
    flow_option: clone(Event.fields.flow_option),
    feedback_form_id: clone(Event.fields.feedback_form_id),
    repeat_pattern: clone(Event.fields.repeat_pattern),
    repeat_start_event_id: clone(Event.fields.repeat_start_event_id),
    repeat_group_id: clone(Event.fields.repeat_group_id),
    repeat_metadata: clone(Event.fields.repeat_metadata),
    created_by: clone(Event.fields.created_by),
    created_at: clone(Event.fields.created_at),
    updated_at: clone(Event.fields.updated_at),
    attendance_token: clone(Event.fields.attendance_token),
    ticket_booking_id: clone(Event.fields.id),
    ticket_id: clone(TicketBooking.fields.ticket_id),
    user_id: clone(BookingView.fields.user_id),
    booked_quantity: clone(BookingView.fields.booked_quantity),
    ticket_quantity: clone(BookingView.fields.ticket_quantity),
    ticket_title: clone(BookingView.fields.ticket_title),
    price: clone(BookingView.fields.price),
    booking_status: clone(TicketBooking.fields.status, { label: 'Booking Status' }),
    permit_self_sign_esig: clone(EventProfile.fields.permit_self_sign_esig),
    institution_group_id: clone(BookingView.fields.institution_group_id),
    attendance_id: clone(BookingView.fields.attendance_id),
    attendance_result: clone(BookingView.fields.attendance_result),
    attendance_recorded_at: clone(BookingView.fields.attendance_recorded_at),
    attendance_recorded_late: clone(BookingView.fields.attendance_recorded_late),
    absence_reason: clone(BookingView.fields.absence_reason),
    event_feedback_id: clone(BookingView.fields.event_feedback_id),
    content_score: clone(BookingView.fields.content_score),
    delivery_score: clone(BookingView.fields.delivery_score),
    admin_score: clone(BookingView.fields.admin_score),
    overall_score: clone(BookingView.fields.overall_score),
    computed_status: clone(BookingView.fields.computed_status),
  },
};
